"use client";

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PreOrderStatus, type PreOrder } from "@/app/api/types/preOrder";
import {
  usePreOrderModifyBatch,
  type PreOrderFlowChange,
} from "@stockline/web";
import { useUserOptional } from "@/components/auth-provider";
import { track } from "@/lib/analytics";
import { aiOrderKind, collectAiPaths } from "@/app/recap/services/recapService";
import { aiInFlight } from "@/app/recap/stores/aiInFlightStore";
import { resolveAiLocally } from "@/app/recap/services/aiResolveLocally/index";
import { useRecapAiSuggestions } from "@/app/recap/hooks/useRecapAiSuggestions";
import { useRecapModeOptional } from "@/app/recap/contexts/RecapContext";

/**
 * Poll the DOM for `selector` across animation frames, up to ~1s (60
 * frames). Resolves with the element when it appears, or `null` on
 * timeout. Tolerates slow SSE-driven re-renders without blocking the
 * caller.
 */
function waitForElement(
  selector: string,
  maxFrames = 60,
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    let frames = 0;
    const tick = (): void => {
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        resolve(el);
        return;
      }
      if (++frames >= maxFrames) {
        resolve(null);
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/**
 * After a bulk approve resolves, jump the user to the area that just
 * changed so they can see the result without scroll-hunting. Header
 * beats flows when both are present; multiple flow paths resolve to
 * the first.
 */
function navigateToApprovedPath(paths: string[], poId: string): void {
  if (paths.length === 0) return;
  const headerPath = paths.find((p) => p === "header");
  const target = headerPath ?? paths.find((p) => p.startsWith("flows."));
  if (!target) return;

  if (target === "header") {
    void waitForElement(`[data-preorder-id="${poId}"]`).then((el) => {
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return;
  }
  const flowId = target.slice("flows.".length);
  void waitForElement(`[data-flow-id="${flowId}"]`).then((el) => {
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    const firstCell = el?.querySelector<HTMLElement>("[data-tab-cell]");
    firstCell?.focus();
  });
}

export interface AiPreOrderActionsState {
  isApproving: boolean;
  isDeclining: boolean;
}

export interface AiPreOrderActions extends AiPreOrderActionsState {
  /**
   * Bulk-resolve every pending AI signal on the PO (header + flows).
   * Runs `resolveAiLocally` to compute the merged push-batch payload —
   * column writes + flipped `ai_suggestions[]` blocks + parent status
   * transition — then ships ONE push-batch. Backend just applies the
   * payload as-is; no server-side AI logic.
   */
  approveAll(po: PreOrder): Promise<void>;
  /**
   * Accept with per-line user edits (AP-4). The caller supplies flow
   * `update` ops whose `data` keys match the AI block's `changes` fields;
   * `resolveAiLocally` marks those fields `modified` with `finalTo` and
   * carries the user's value into the push-batch. No edits → use approveAll.
   */
  approveWithChanges(
    po: PreOrder,
    flowChanges: PreOrderFlowChange[],
  ): Promise<void>;
  /**
   * Mirror of `approveAll` for decline. Wholesale-decline of an
   * AI_PROPOSAL parent soft-deletes the parent row (recap "restore"
   * brings the original proposal back).
   */
  declineAll(po: PreOrder): Promise<void>;
}

/**
 * Approve / decline mutations for one pre-order.
 *
 * **All AI semantics live frontend-side.** The resolver
 * (`resolveAiLocally`) computes the final merged state — column
 * writes, flipped `ai_suggestions[]` blocks, parent `AI_PROPOSAL →
 * PENDING` promotion, wholesale-decline soft-delete — and the result
 * rides ONE `push-batch` call. Backend is a generic CRUD endpoint
 * with no knowledge of AI.
 */
export function useAiPreOrderActions(preOrderId: string): AiPreOrderActions {
  const { t } = useTranslation();
  const modifyBatch = usePreOrderModifyBatch();
  const user = useUserOptional();
  // Tags the AI accept/decline events with the recap workspace mode when this
  // hook runs inside one (sales / purchases). In the standalone /ai-pre-orders
  // queue there's no RecapProvider — the optional read returns null and `mode`
  // is simply omitted (that surface is identified by its own pathname instead).
  const recapMode = useRecapModeOptional() ?? undefined;
  const { rowIndex: aiRowIndex, stampResolutions } = useRecapAiSuggestions();
  const [state, setState] = useState<AiPreOrderActionsState>({
    isApproving: false,
    isDeclining: false,
  });

  const runResolve = useCallback(
    async (
      po: PreOrder,
      decision: "accept" | "decline",
      flowChanges: PreOrderFlowChange[] = [],
    ): Promise<void> => {
      const isApprove = decision === "accept";
      setState((s) => ({
        ...s,
        ...(isApprove ? { isApproving: true } : { isDeclining: true }),
      }));
      if (isApprove) aiInFlight.setApprovingAi(preOrderId, true);
      else aiInFlight.setDecliningAi(preOrderId, true);
      try {
        const resolved = resolveAiLocally(
          {
            userId: user?.id ?? "",
            resolvedAt: new Date().toISOString(),
          },
          po,
          { preOrderId, flowChanges },
          decision,
          aiRowIndex,
        );
        // Fire the AI funnel event from pushBatch's onSuccess — which runs
        // after the POST + reconcile in BOTH await and background modes — so a
        // background decline whose server write later reverts never inflates
        // the count. `flow_count` excludes the "header" path (flows only).
        const emitResolved = () => {
          track(isApprove ? "ai_proposal_accepted" : "ai_proposal_declined", {
            pre_order_id: preOrderId,
            scope: "all",
            flow_count: collectAiPaths(po).filter((p) => p.startsWith("flows."))
              .length,
            mode: recapMode,
            order_kind: aiOrderKind(po),
          });
          // Stamp the sidecar suggestion rows accepted/declined — runs after
          // POST + reconcile in both await and background modes, so a write
          // that later reverts never leaves a stranded resolution stamp.
          void stampResolutions(resolved.resolutions);
        };
        if (isApprove) {
          // Accept can re-export on a CONFIRMED parent (the AI's accepted
          // column writes change order data, so the implicit re-export is the
          // correct gesture), so keep the spinner until the server responds.
          // PENDING / AI_PROPOSAL accepts keep `postBatchStatus=0` and never
          // trip the heuristic.
          await modifyBatch.pushBatch(resolved.batch, {
            onSuccess: emitResolved,
          });
          toast.success(t("aiProposals.toast.approved"));
        } else {
          // Decline never re-exports — `intent: "draft"` opts out of the
          // handler's implicit-confirm heuristic (a CONFIRMED-parent decline
          // only flips `ai_suggestions[]` blocks pending → declined; the ERP
          // doesn't need to hear about it). Since there's no ERP wait, push in
          // the BACKGROUND so the spinner clears the moment the optimistic
          // apply lands — instant, like delete. Errors revert + toast from the
          // settle callbacks.
          await modifyBatch.pushBatch(
            { ...resolved.batch, intent: "draft" as const },
            {
              background: true,
              onSuccess: () => {
                emitResolved();
                toast.success(t("aiProposals.toast.declined"));
              },
              onError: (err) =>
                toast.error(
                  t("aiProposals.toast.declineFailed", {
                    error: err.message,
                  }),
                ),
            },
          );
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error";
        toast.error(
          isApprove
            ? t("aiProposals.toast.approveFailed", { error: message })
            : t("aiProposals.toast.declineFailed", { error: message }),
        );
        throw err;
      } finally {
        setState((s) => ({
          ...s,
          ...(isApprove ? { isApproving: false } : { isDeclining: false }),
        }));
        if (isApprove) aiInFlight.setApprovingAi(preOrderId, false);
        else aiInFlight.setDecliningAi(preOrderId, false);
      }
    },
    [
      aiRowIndex,
      modifyBatch,
      preOrderId,
      recapMode,
      stampResolutions,
      t,
      user?.id,
    ],
  );

  const approveAll = useCallback(
    async (po: PreOrder): Promise<void> => {
      const paths = collectAiPaths(po);
      // An AI_PROPOSAL parent with no pending blocks is still actionable:
      // the row itself is the proposal, so accept must promote the
      // parent to PENDING. `resolveAiLocally` handles the empty-paths
      // case for status=AI_PROPOSAL — skip the early-out toast and let
      // it run.
      const isAiProposalParent = po.status === PreOrderStatus.AI_PROPOSAL;
      if (paths.length === 0 && !isAiProposalParent) {
        toast.warning(t("aiProposals.toast.nothingToApprove"));
        return;
      }
      await runResolve(po, "accept");
      navigateToApprovedPath(paths, po.id);
    },
    [runResolve, t],
  );

  const approveWithChanges = useCallback(
    async (po: PreOrder, flowChanges: PreOrderFlowChange[]): Promise<void> => {
      if (flowChanges.length === 0) {
        await approveAll(po);
        return;
      }
      const paths = collectAiPaths(po);
      await runResolve(po, "accept", flowChanges);
      navigateToApprovedPath(paths, po.id);
    },
    [approveAll, runResolve],
  );

  const declineAll = useCallback(
    async (po: PreOrder): Promise<void> => {
      const paths = collectAiPaths(po);
      // Mirror of approveAll: decline on a status=AI_PROPOSAL parent
      // with no pending blocks soft-deletes the parent (wholesale-
      // decline). The resolver covers the empty-paths case directly.
      const isAiProposalParent = po.status === PreOrderStatus.AI_PROPOSAL;
      if (paths.length === 0 && !isAiProposalParent) {
        toast.warning(t("aiProposals.toast.nothingToDecline"));
        return;
      }
      await runResolve(po, "decline");
    },
    [runResolve, t],
  );

  return {
    ...state,
    approveAll,
    approveWithChanges,
    declineAll,
  };
}
